"use client"

import { useState, useEffect } from "react"
import { X, Sparkles, ArrowRight, Lightbulb, Target, Brain } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import type { QuickWin } from "@/src/intelligence/quick-wins"

interface QuickWinsToastProps {
  wins: QuickWin[]
  onDismiss: () => void
  onAction?: (win: QuickWin) => void
}

const iconMap = {
  tip: Lightbulb,
  action: Target,
  insight: Sparkles,
  next_memory: Brain,
}

const bgMap = {
  tip: "bg-amber-500/10",
  action: "bg-green-500/10",
  insight: "bg-primary/10",
  next_memory: "bg-purple-500/10",
}

const iconColorMap = {
  tip: "text-amber-400",
  action: "text-green-400",
  insight: "text-primary",
  next_memory: "text-purple-400",
}

export function QuickWinsToast({ wins, onDismiss, onAction }: QuickWinsToastProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    // Animate in after a short delay
    const timer = setTimeout(() => setIsVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Auto-dismiss after 8 seconds if no interaction
    const timer = setTimeout(() => {
      handleDismiss()
    }, 8000)
    return () => clearTimeout(timer)
  }, [])

  const handleDismiss = () => {
    setIsVisible(false)
    setTimeout(onDismiss, 300) // Wait for animation
  }

  const handleAction = (win: QuickWin) => {
    if (onAction) {
      onAction(win)
    }
    handleDismiss()
  }

  const currentWin = wins[currentIndex]
  if (!currentWin) return null

  const Icon = iconMap[currentWin.type]

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 w-80 transition-all duration-300",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
      )}
    >
      <div className="bg-surface-elevated rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className={cn("px-4 py-3 flex items-center gap-3", bgMap[currentWin.type])}>
          <Icon className={cn("h-5 w-5", iconColorMap[currentWin.type])} />
          <span className="flex-1 font-medium text-sm text-foreground">{currentWin.title}</span>
          <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3">
          <p className="text-sm text-muted-foreground leading-relaxed">{currentWin.description}</p>

          {/* Action Button */}
          {currentWin.actionLabel && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full bg-transparent text-primary hover:bg-primary/10"
              onClick={() => handleAction(currentWin)}
            >
              {currentWin.actionLabel}
              <ArrowRight className="h-3.5 w-3.5 ml-2" />
            </Button>
          )}
        </div>

        {/* Pagination dots */}
        {wins.length > 1 && (
          <div className="px-4 pb-3 flex justify-center gap-1.5">
            {wins.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={cn(
                  "w-1.5 h-1.5 rounded-full transition-colors",
                  i === currentIndex ? "bg-primary" : "bg-border",
                )}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
